import {
    ACTION_TYPE_FILTER_NONE,
    ACTION_TYPE_FILTER_OPTIONS,
    AllFilters,
    Claim,
    CLAIM_DISPOSITION_NONE,
    CLAIM_DISPOSITION_OPTIONS,
    CLAIM_STATUS_OPTIONS,
    ColumnFilterEntry,
    FilterOperator,
    LINKED_FILTER_OPTIONS,
} from '../../types/claims';
import { ColumnFilterState } from '../../types/table';
import { SelectOption } from '../ui/core/CoreSelect';

// Column filter rules for claims tables, shared by the Claims page (ClaimsStore,
// ClaimsTable) and the home page's "Your Claims" table, so a column added here
// reaches both in the same request shape.

// Columns that get a filter popover.
export const CLAIMS_FILTERABLE_COLUMNS = [
    'claimNumber',
    'patientName',
    'medicalRecordNumber',
    'dateOfService',
    'dob',
    'payer',
    'procedureCode',
    'diagnosisCode',
    'billedAmount',
    'paidAmount',
    'patientBalance',
    'status',
    'claimType',
    'denialReason',
    'assignedUser',
    'placeOfService',
    'dateSubmitted',
    'appealLetter',
    'appealed',
    'actionType',
    'linked',
    'paidInclResubmission',
    'disposition',
    'updatedAt',
    'createdAt',
];

// Date ranges are sent as a plain [from, to] array, everything else as
// { operator, values }.
const DATE_RANGE_COLUMNS = ['dateOfService', 'dob', 'dateSubmitted'];

// The filter UI shows claim type names, the backend takes letters.
const CLAIM_TYPE_NAME_TO_VALUE: Record<string, string> = {
    Institutional: 'I',
    Professional: 'P',
    Dental: 'D',
};
const CLAIM_TYPE_VALUE_TO_NAME: Record<string, string> = Object.fromEntries(
    Object.entries(CLAIM_TYPE_NAME_TO_VALUE).map(([name, value]) => [value, name]),
);

/** `filters` with `columnId`'s filter set to the UI's `columnValues` (none clears it). */
export const withClaimsColumnFilter = (
    filters: AllFilters | null,
    columnId: string,
    columnValues: string[],
    operator: FilterOperator = 'and',
): AllFilters | null => {
    const next = { ...filters } as Record<string, unknown>;
    // Exclude empty values to send to backend
    let values = columnValues?.filter((value) => !!value) ?? [];
    // An open-ended [min, max] range keeps its blank end, so the backend knows
    // which bound it got (REV-2634)
    if (columnId === 'paidInclResubmission' && values.length > 0) {
        values = [columnValues[0] || '', columnValues[1] || ''];
    }
    if (columnId === 'claimType') {
        values = values.map((value) => CLAIM_TYPE_NAME_TO_VALUE[value] || value);
    }

    if (values.length === 0) {
        delete next[columnId];
    } else if (DATE_RANGE_COLUMNS.includes(columnId)) {
        next[columnId] = values;
    } else {
        next[columnId] = { operator, values } as ColumnFilterEntry;
    }
    return Object.keys(next).length > 0 ? (next as AllFilters) : null;
};

/** `columnId`'s filter in `filters`, as the filter UI shows it. */
export const getClaimsColumnFilterState = (
    filters: AllFilters | null,
    columnId: string,
): ColumnFilterState => {
    const raw = filters?.[columnId as keyof AllFilters];
    let operator: FilterOperator = 'and';
    let values: string[] = [];
    if (Array.isArray(raw)) {
        values = raw as string[];
    } else if (raw && typeof raw === 'object' && 'values' in raw) {
        const entry = raw as ColumnFilterEntry;
        values = (entry.values as string[]) ?? [];
        if (entry.operator === 'or' || entry.operator === 'isnot') {
            operator = entry.operator;
        }
    }
    if (columnId === 'claimType') {
        values = values.map((value) => CLAIM_TYPE_VALUE_TO_NAME[value] || value);
    }
    return { operator, values };
};

/**
 * Filter options for `columnId`: fixed sets where there are some, then the
 * backend's option lists, otherwise the applied values or those in the loaded
 * rows.
 */
export const getClaimsColumnOptions = (
    columnId: string,
    {
        filterOptions,
        filters,
        claims,
    }: {
        filterOptions: Record<string, SelectOption[]> | null | undefined;
        filters: AllFilters | null;
        claims: Claim[] | null;
    },
): string[] => {
    // Fixed sets: independent of backend options and of what the loaded page
    // happens to contain.
    switch (columnId) {
        case 'appealLetter':
        case 'appealed':
            return ['Yes', 'No'];
        case 'disposition':
            return [...CLAIM_DISPOSITION_OPTIONS, CLAIM_DISPOSITION_NONE];
        case 'actionType':
            return [...ACTION_TYPE_FILTER_OPTIONS, ACTION_TYPE_FILTER_NONE];
        case 'linked':
            return LINKED_FILTER_OPTIONS;
    }

    const backendOptions = filterOptions?.[columnId];
    if (backendOptions) {
        return backendOptions.map((option) => option.label);
    }

    if (!claims) return [];

    let values: string[];
    switch (columnId) {
        case 'status':
            values = CLAIM_STATUS_OPTIONS.map((status) => status.value as string);
            break;
        case 'claimType':
            values = ['Institutional', 'Professional'];
            break;
        case 'assignedUser':
            values = (filterOptions?.assignedUser ?? []).map((user) => user.label);
            break;
        default: {
            const applied = getClaimsColumnFilterState(filters, columnId).values;
            values = applied.length
                ? applied
                : claims
                    .map((claim) => claim[columnId as keyof Claim])
                    .filter((value) => value != null && value !== '')
                    .map(String);
        }
    }
    return Array.from(new Set(values)).sort();
};
